'use client';

import clsx from 'classnames';
import { ChangeEvent, MouseEvent, useState } from 'react';

import styles from './NumberSelector.module.scss';
import { NumberSelectorProps } from './types';

export const NumberSelector = ({
  defaultValue,
  endLabel,
  endLabelClassName,
  startLabel,
  startLabelClassName,
  max,
  min,
  value,
  variant = 'compact',
  onChange,
  validateChange,
}: NumberSelectorProps) => {
  const [number, setNumber] = useState<number>(defaultValue ?? 0);

  const inputWrapperClasNames = clsx(
    styles.numberSelector__inputWrapper,
    styles[`numberSelector__inputWrapper--${variant}`]
  );
  const startLabelClassNames = clsx(
    styles.numberSelector__startLabel,
    startLabelClassName
  );
  const endLabelClassNames = clsx(
    styles.numberSelector__endLabel,
    endLabelClassName
  );

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const newValue = Number(event.target.value);

    if (onChange) {
      if (max !== undefined && newValue > max) {
        onChange(max);
        return;
      }

      if (min !== undefined && newValue < min) {
        onChange(min);
        return;
      }

      onChange(newValue);
      return;
    }

    if (max !== undefined && newValue > max) {
      setNumber(max);
      return;
    }

    if (min !== undefined && newValue < min) {
      setNumber(min);
      return;
    }

    setNumber(newValue);
  };

  const handleInputBlur = async (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const newValue = Number(event.target.value);

    if (newValue === defaultValue) {
      return;
    }

    if (!validateChange) {
      return;
    }

    const isValid = await validateChange(newValue);

    if (isValid) {
      return;
    }

    setNumber(defaultValue ?? 1);
  };

  const handleAddClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (isAddButtonDisabled()) {
      return;
    }

    if (onChange !== undefined && value !== undefined) {
      onChange((value += 1));
      return;
    }

    const newValue = number + 1;

    if (!validateChange) {
      setNumber(newValue);
      return;
    }

    const isValid = await validateChange(newValue);

    if (isValid) {
      setNumber(newValue);
      return newValue;
    }

    return;
  };

  const handleMinusClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (isMinusButtonDisabled()) {
      return;
    }

    if (onChange !== undefined && value !== undefined) {
      onChange((value -= 1));
      return;
    }

    const newValue = number - 1;

    if (!validateChange) {
      setNumber(newValue);
      return;
    }

    const isValid = await validateChange(newValue);

    if (isValid) {
      setNumber(newValue);
      return newValue;
    }

    return;
  };

  const isAddButtonDisabled = () => {
    if (max === undefined) {
      return false;
    }

    if (value !== undefined) {
      return value >= max;
    }

    return number >= max;
  };

  const isMinusButtonDisabled = () => {
    if (min === undefined) {
      return false;
    }

    if (value !== undefined) {
      return value <= min;
    }

    return number <= min;
  };

  return (
    <div className={styles.numberSelector}>
      {startLabel && <span className={startLabelClassNames}>{startLabel}</span>}
      <div className={inputWrapperClasNames}>
        <button
          className={clsx(styles.numberSelector__buttons, {
            [styles['numberSelector__buttons--disabled']]:
              isMinusButtonDisabled(),
          })}
          onClick={handleMinusClick}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8z" />
          </svg>
        </button>
        <input
          className={styles.numberSelector__textField}
          max={max}
          min={min}
          onBlur={handleInputBlur}
          onClick={(event) => event.preventDefault()}
          onChange={handleInputChange}
          type="number"
          value={value ?? number}
          suppressHydrationWarning
        />

        <button
          className={clsx(styles.numberSelector__buttons, {
            [styles['numberSelector__buttons--disabled']]:
              isAddButtonDisabled(),
          })}
          onClick={handleAddClick}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
          </svg>
        </button>
      </div>
      {endLabel && <span className={endLabelClassNames}>{endLabel}</span>}
    </div>
  );
};
